import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db, auth } from './firebaseConfig';
import SocialLogin from './components/SocialLogin';
import Register from './components/Register';


import ErrorBoundary from './components/ErrorBoundary';
import MainLayout from './components/MainLayout';
import ArticleDetail from './components/ArticleDetail';
import AdminLogin from './components/AdminLogin';
import AdminPanel from './components/AdminPanel';
import TechTools from './components/TechTools';
import CategoryPage from './components/CategoryPage';

// 🎮 თამაშების იმპორტები
import GamesPage from './components/game/GamesPage';
import PuzzleGame from './components/game/PuzzleGame';
import MemoryGame from './components/game/MemoryGame';
import CodeQuiz from './components/game/CodeQuiz';
import TypingSpeed from './components/game/TypingSpeed';
import BinaryConverter from './components/game/BinaryConverter';
import LogicPuzzle from './components/game/LogicPuzzle';
import DragDropGame from './components/game/DragDropGame';
import BugHunter from './components/game/BugHunter';
import KeyboardShortcuts from './components/game/KeyboardShortcut';
import CodeRunner from './components/game/CodeRunner';

// 🆕 ახალი თამაშების იმპორტები
import JigsawGame from './components/game/JigsawGame';
import SortingGame from './components/game/SortingGame';
import MathQuiz from './components/game/MathQuiz';

// 🏆 Score System
import { ScoreProvider } from './context/ScoreContext';
import Scoreboard from './components/Scoreboard';

// ============================================
// AppContent კომპონენტი
// ============================================
function AppContent() {
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  // Auth
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  // მონაცემები
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const catSnap = await getDocs(collection(db, 'categories'));
      const catList = catSnap.docs.map(doc => doc.data().name);
      setCategories(catList);

      const artSnap = await getDocs(collection(db, 'articles'));
      const artList = artSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      artList.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setArticles(artList);
    } catch (error) {
      console.error("Firebase-იდან მონაცემების წამოღების შეცდომა:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // წაშლა
  const handleDeleteArticle = useCallback(async (id) => {
    if (window.confirm('დარწმუნებული ხართ, რომ გსურთ სტატიის წაშლა?')) {
      try {
        await deleteDoc(doc(db, 'articles', id));
        alert('სტატია წარმატებით წაიშალა!');
        fetchAllData();
      } catch (error) {
        console.error("წაშლის შეცდომა:", error);
        alert('სტატია ვერ წაიშალა.');
      }
    }
  }, [fetchAllData]);

  return (
    <Routes>
      {/* მთავარი */}
      <Route path="/" element={
        <MainLayout 
          isDarkMode={isDarkMode} 
          setIsDarkMode={setIsDarkMode} 
          articles={articles} 
          categories={categories} 
          loading={loading} 
        />
      } />
      
      {/* სტატიები */}
      <Route path="/category/:categoryName" element={<CategoryPage isDarkMode={isDarkMode} />} />
      <Route path="/article/:id" element={<ArticleDetail isDarkMode={isDarkMode} />} />
      <Route path="/register" element={<Register isDarkMode={isDarkMode} />} />
      
      {/* ადმინი */}
      <Route path="/login" element={<AdminLogin isDarkMode={isDarkMode} />} />
      <Route path="/admin" element={
        <AdminPanel 
          isDarkMode={isDarkMode} 
          categories={categories} 
          articles={articles}
          currentUser={currentUser}
          onCategoryAdded={fetchAllData} 
          onArticleAdded={fetchAllData} 
          handleDeleteArticle={handleDeleteArticle}
        />
      } />
      
      {/* ინსტრუმენტები */}
      <Route path="/tools" element={<TechTools isDarkMode={isDarkMode} />} />
      
      {/* 🏆 Scoreboard */}
      <Route path="/scoreboard" element={<Scoreboard isDarkMode={isDarkMode} />} />
      
      {/* 🎮 თამაშები */}
      <Route path="/games" element={<GamesPage isDarkMode={isDarkMode} />} />
      <Route path="/puzzle" element={<PuzzleGame isDarkMode={isDarkMode} />} />
      <Route path="/memory" element={<MemoryGame isDarkMode={isDarkMode} />} />
      <Route path="/code-quiz" element={<CodeQuiz isDarkMode={isDarkMode} />} />
      <Route path="/typing" element={<TypingSpeed isDarkMode={isDarkMode} />} />
      <Route path="/binary" element={<BinaryConverter isDarkMode={isDarkMode} />} />
      <Route path="/logic" element={<LogicPuzzle isDarkMode={isDarkMode} />} />
      <Route path="/dragdrop" element={<DragDropGame isDarkMode={isDarkMode} />} />
      <Route path="/bug" element={<BugHunter isDarkMode={isDarkMode} />} />
      <Route path="/keyboard" element={<KeyboardShortcuts isDarkMode={isDarkMode} />} />
      <Route path="/code-runner" element={<CodeRunner isDarkMode={isDarkMode} />} />
      
      {/* 🆕 ახალი თამაშების Route-ები */}
      <Route path="/jigsaw" element={<JigsawGame isDarkMode={isDarkMode} />} />
      <Route path="/sorting" element={<SortingGame isDarkMode={isDarkMode} />} />
      <Route path="/math-quiz" element={<MathQuiz isDarkMode={isDarkMode} />} />
      
      {/* 404 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// ============================================
// მთავარი App კომპონენტი
// ============================================
export default function App() {
  return (
    
    <Router>
      <SocialLogin />
      <ErrorBoundary>
        <ScoreProvider>
          <AppContent />
        </ScoreProvider>
      </ErrorBoundary>
    </Router>
  );
}